import { useTranslation } from 'react-i18next';
import { AdblockSummary } from '../../../types/adblock';
import { Card, CardContent } from '../../ui/card';
import { Skeleton } from '../../ui/skeleton';
import { 
  Eye, 
  BarChart3, 
  MousePointer, 
  DollarSign 
} from 'lucide-react';

interface AdblockSummaryCardsProps {
  summary: AdblockSummary;
  isLoading: boolean;
}

export function AdblockSummaryCards({ summary, isLoading }: AdblockSummaryCardsProps) {
  const { t } = useTranslation();

  const formatNumber = (value: number): string => {
    if (value === undefined || value === null || isNaN(value)) {
      return '0';
    }
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(2)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(2)}k`;
    }
    return value.toLocaleString();
  };

  const formatCurrency = (value: number): string => {
    if (value === undefined || value === null || isNaN(value)) {
      return '$0.00';
    }
    return `$${value.toFixed(2)}`;
  };

  const formatPercentage = (value: number): string => {
    if (value === undefined || value === null || isNaN(value)) {
      return '0.00%';
    }
    return `${value.toFixed(2)}%`;
  };

  const summaryCards = [
    {
      title: t('adblock.dashboard.summary.adblockPageviews'),
      value: summary.totalPageviewsBlockingAds,
      icon: Eye,
      formatter: formatNumber,
      gradient: 'from-blue-500 to-blue-600'
    },
    {
      title: t('adblock.dashboard.summary.blockingRate'),
      value: summary.blockingRate,
      icon: BarChart3,
      formatter: formatPercentage,
      gradient: 'from-red-500 to-red-600'
    },
    {
      title: t('adblock.dashboard.summary.totalImpressions'),
      value: summary.totalImpressions,
      icon: MousePointer,
      formatter: formatNumber,
      gradient: 'from-green-500 to-green-600'
    },
    {
      title: t('adblock.dashboard.summary.totalRevenue'),
      value: summary.totalRevenueUSD,
      icon: DollarSign,
      formatter: formatCurrency,
      gradient: 'from-orange-500 to-orange-600'
    }
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {summaryCards.map((card, index) => {
          const IconComponent = card.icon;
          
          return (
            <Card key={index} className={`border-none shadow-sm bg-gradient-to-r ${card.gradient}`}>
              <CardContent className="p-6 text-white">
                {isLoading ? (
                  <>
                    <div className="flex items-center justify-between space-y-0 pb-2">
                      <Skeleton className="h-4 w-24 bg-white/20" />
                      <Skeleton className="h-6 w-6 rounded-full bg-white/20" />
                    </div>
                    <Skeleton className="h-8 w-20 bg-white/20 mt-2" />
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-between space-y-0 pb-2">
                      <h3 className="tracking-tight text-sm font-medium opacity-90">
                        {card.title}
                      </h3>
                      <IconComponent className="h-6 w-6 opacity-90" />
                    </div>
                    <div className="text-2xl font-bold">
                      {card.formatter(card.value)}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}